import { Link, useParams } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/categories';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';

const DOMAIN = 'https://jianxiaoer.cn';

export default function TopicPage() {
  const { slug = '' } = useParams();
  const topic = topics.find((item) => item.slug === slug);
  const category = topic ? categories.find((cat) => cat.slug === topic.categorySlug) : undefined;
  const relatedTopics = topic
    ? topic.related.map((rs) => topics.find((t) => t.slug === rs)).filter((t): t is NonNullable<typeof t> => Boolean(t))
    : [];

  useSEO({
    title: topic ? `${topic.title} | 爆料前线` : '话题不存在 | 爆料前线',
    description: topic
      ? `${topic.summary.slice(0, 80)}……实时跟进事件进展、关键时间节点与相关话题。`
      : '该话题暂不存在，返回首页查看当前正在冲榜的热点。',
    keywords: topic ? `${topic.tags.join(',')},实时热点,突发新闻,事件进展` : '爆料平台,实时热点',
    path: `/topic/${slug}`,
    ogType: 'article',
    jsonLd: topic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: topic.title,
            description: topic.summary,
            inLanguage: 'zh-CN',
            articleSection: category ? category.name : '热点',
            datePublished: topic.publishedAt,
            dateModified: topic.publishedAt,
            keywords: topic.tags.join(','),
            url: `${DOMAIN}/topic/${topic.slug}`,
            publisher: { '@type': 'Organization', name: '爆料前线', url: `${DOMAIN}/` },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${DOMAIN}/` },
              ...(category
                ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `${DOMAIN}/category/${category.slug}` }]
                : []),
              { '@type': 'ListItem', position: category ? 3 : 2, name: topic.title, item: `${DOMAIN}/topic/${topic.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-question-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">这个话题已经找不到了</h1>
        <p className="text-sm text-foreground-600">可能是链接有误，或者话题已被合并到其他条目中。</p>
        <Link
          to="/ranking"
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          去看全网热搜榜
        </Link>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
    { label: topic.title },
  ];

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-800">{topic.tag}</span>
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-500">更新于 {topic.timeAgo}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-fire-line text-primary-600" />
                </span>
                热度 {(topic.heat / 10000).toFixed(1)} 万
              </span>
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-eye-line" />
                </span>
                {topic.readCount} 次阅读
              </span>
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-chat-3-line" />
                </span>
                {topic.commentCount} 条讨论
              </span>
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                {topic.publishedAt}
              </span>
            </div>

            <figure className="mt-6 overflow-hidden rounded-lg border border-background-200">
              <div className="h-60 w-full md:h-96">
                <img src={topic.cover} alt={topic.coverAlt} className="h-full w-full object-cover object-top" />
              </div>
              <figcaption className="bg-background-100 px-4 py-3 text-xs text-foreground-500">
                图片来源：爆料前线编辑配图 · {topic.coverAlt}
              </figcaption>
            </figure>

            <div className="mt-7 flex flex-col gap-5">
              {topic.paragraphs.map((text, index) => (
                <p key={index} className="text-sm leading-loose text-foreground-800 md:text-base">
                  {text}
                </p>
              ))}
            </div>

            <section className="mt-9 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6" aria-labelledby="detail-timeline">
              <h2 id="detail-timeline" className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-timer-line text-xl" />
                </span>
                事件时间线
              </h2>
              <ol className="mt-5 relative border-l border-background-300 pl-5 md:pl-6">
                {topic.timeline.map((node, index) => (
                  <li key={index} className="relative pb-5 last:pb-0">
                    <span
                      className={`absolute -left-[27px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-background-100 md:-left-[31px] ${
                        index === topic.timeline.length - 1 ? 'bg-primary-500' : 'bg-secondary-400'
                      }`}
                    />
                    <span className="font-heading text-sm font-bold text-foreground-950">{node.time}</span>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{node.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <div className="mt-7 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">话题标签：</span>
              {topic.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100 px-5 py-4">
              <p className="text-sm text-foreground-700">
                有关于这条话题的一手信息？欢迎补充细节，编辑会尽快核实。
              </p>
              <Link
                to="/contact"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-send-plane-line text-sm" />
                </span>
                我要补充线索
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">相关话题</h2>
              <ul className="mt-4 flex flex-col gap-3">
                {relatedTopics.map((item) => (
                  <li key={item.slug} className="border-b border-background-200 pb-3 last:border-b-0 last:pb-0">
                    <Link
                      to={`/topic/${item.slug}`}
                      className="line-clamp-2 cursor-pointer text-sm leading-relaxed text-foreground-800 transition-colors hover:text-primary-700"
                    >
                      {item.title}
                    </Link>
                    <span className="mt-1 block text-[11px] text-foreground-500">
                      {item.timeAgo} · 热度 {(item.heat / 10000).toFixed(1)} 万
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">继续逛</h2>
              <div className="mt-4 flex flex-col gap-2.5">
                <Link
                  to="/ranking"
                  className="flex cursor-pointer items-center justify-between rounded-md border border-background-300 px-3.5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  全网热搜榜
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </Link>
                <Link
                  to="/breaking"
                  className="flex cursor-pointer items-center justify-between rounded-md border border-background-300 px-3.5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  突发新闻速递
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </Link>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="flex cursor-pointer items-center justify-between rounded-md border border-background-300 px-3.5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {category.name}频道
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-line text-base" />
                    </span>
                  </Link>
                )}
              </div>
            </div>
          </aside>
        </div>

        {relatedTopics.length > 0 && (
          <section className="mt-12 border-t border-background-200 pt-10" aria-labelledby="related-heading">
            <h2 id="related-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              站内推荐
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {relatedTopics.map((item) => (
                <TopicCard
                  key={item.slug}
                  topic={item}
                  categoryName={categories.find((cat) => cat.slug === item.categorySlug)?.name}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}